import { createForm } from "@tanstack/solid-form";
import { createSignal, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import { CategorySelect } from "@/domains/freelance/taxonomy/components/CategorySelect";
import { categoryLabel } from "@/domains/freelance/taxonomy/store/taxonomy-store";
import type { JobCategory } from "@/domains/job-offer/create/constants";
import { useRefineStore } from "../../store/RefineProvider";
import {
  changeOfferCategory,
  visibleSubcategories,
} from "../../utils/offer-subcategories";
import { titleCategoryEditSchema } from "../../validators";
import { RefineEditDialog } from "../RefineEditDialog";
import { RefineStepCard } from "../RefineStepCard";
import { SubcategoryChoiceField } from "../SubcategoryChoiceField";
import { SubcategorySummary } from "../SubcategorySummary";

export function TitleCategoryStep() {
  const { t } = useI18n();
  const { state, updateFields } = useRefineStore();
  const [editOpen, setEditOpen] = createSignal(false);
  /* Interaction state of the dialog, written only by its handlers: the
     category the subcategories belong to (see `changeOfferCategory`) and
     whether the dialog says that a change of category removed them. */
  const [valueCategory, setValueCategory] = createSignal<JobCategory | null>(
    null,
  );
  const [notice, setNotice] = createSignal(false);

  const form = createForm(() => ({
    defaultValues: {
      title: state.title,
      job_category: state.job_category,
      subcategories: visibleSubcategories(
        state.job_category,
        state.subcategories,
      ),
    },
    onSubmit: ({ value }) => {
      const result = titleCategoryEditSchema.safeParse(value);
      if (!result.success) return;
      updateFields({
        title: result.data.title,
        job_category: result.data.job_category,
        subcategories: result.data.subcategories,
      });
      setEditOpen(false);
    },
  }));

  const handleOpenEdit = () => {
    form.reset();
    form.setFieldValue("title", state.title);
    form.setFieldValue("job_category", state.job_category);
    form.setFieldValue(
      "subcategories",
      visibleSubcategories(state.job_category, state.subcategories),
    );
    setValueCategory(state.job_category);
    setNotice(false);
    setEditOpen(true);
  };

  const formCategory = form.useStore((s) => s.values.job_category);

  return (
    <>
      <RefineStepCard
        title={t("jobOffers.refine.steps.titleCategory")}
        onEdit={handleOpenEdit}
      >
        <div class="space-y-2">
          <p class="text-sm text-muted-foreground">
            {t("jobOffers.refine.titleLabel")}
          </p>
          <p class="font-medium">{state.title || "—"}</p>

          <p class="text-sm text-muted-foreground mt-3">
            {t("jobOffers.refine.categoryLabel")}
          </p>
          <Show
            when={state.job_category}
            fallback={
              // Not `text-destructive`: on white it is 3.76:1, below AA.
              <p class="text-sm text-red-600">
                {t("jobOffers.refine.subcategories.missingCategory")}
              </p>
            }
          >
            {(cat) => (
              <Badge variant="light-primary">{categoryLabel(cat())}</Badge>
            )}
          </Show>
          <SubcategorySummary
            value={visibleSubcategories(
              state.job_category,
              state.subcategories,
            )}
          />
        </div>
      </RefineStepCard>

      <RefineEditDialog
        open={editOpen()}
        onOpenChange={setEditOpen}
        title={t("jobOffers.refine.editTitleCategory")}
      >
        <div class="space-y-4">
          <form.Field
            name="title"
            validators={{
              onChange: titleCategoryEditSchema.shape.title,
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for={field().name}>
                  {t("jobOffers.refine.titleLabel")}
                </FieldContainerLabel>
                <Input
                  id={field().name}
                  name={field().name}
                  value={field().state.value ?? ""}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  onBlur={field().handleBlur}
                />
                <FieldInfo field={field()} />
              </FieldContainer>
            )}
          </form.Field>

          <form.Field
            name="job_category"
            validators={{
              onChange: titleCategoryEditSchema.shape.job_category,
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for={field().name}>
                  {t("jobOffers.refine.categoryLabel")}
                </FieldContainerLabel>
                <CategorySelect
                  value={field().state.value}
                  onChange={(next) => {
                    const result = changeOfferCategory({
                      valueCategory: valueCategory(),
                      current: form.getFieldValue("subcategories"),
                      next,
                    });
                    field().handleChange(next);
                    form.setFieldValue("subcategories", result.value);
                    setValueCategory(result.valueCategory);
                    if (result.notice !== null) setNotice(result.notice);
                  }}
                  placeholder={t(
                    "onboarding.steps.skills.selectCategoryPlaceholder",
                  )}
                />
                <FieldInfo field={field()} />
              </FieldContainer>
            )}
          </form.Field>

          <div class="space-y-1">
            <form.Field name="subcategories">
              {(field) => (
                <SubcategoryChoiceField
                  category={formCategory()}
                  value={field().state.value}
                  onChange={(value) => field().handleChange(value)}
                />
              )}
            </form.Field>
            <p role="status" class="px-2 text-xs text-foreground/70">
              <Show when={notice()}>
                {t("jobOffers.refine.subcategories.categoryChanged")}
              </Show>
            </p>
          </div>

          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={form.handleSubmit}>{t("common.save")}</Button>
          </div>
        </div>
      </RefineEditDialog>
    </>
  );
}
